import { supabase } from '@/lib/supabase';

// sellers 테이블 구조에 맞는 타입 정의
// 주의: nickname, profile_image_url, introduction, specialties는 
// migration 20250124001에서 제거됨
export interface SellerProfile {
  user_id: string;
  marketing_consent: boolean;
  status: 'pending_approval' | 'approved' | 'suspended' | 'rejected';
  created_at: string;
  updated_at: string;
}

export interface SellerCreate {
  user_id: string;
  marketing_consent: boolean;
}

export interface SellerUpdate {
  marketing_consent?: boolean;
}

/**
 * 판매자 프로필 생성
 */
export async function createSellerProfile(
  sellerData: SellerCreate
): Promise<{ success: boolean; data?: SellerProfile; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('sellers')
      .insert({
        user_id: sellerData.user_id,
        marketing_consent: sellerData.marketing_consent,
        status: 'pending_approval' // 기본값: 승인 대기
      })
      .select()
      .single();

    if (error) {
      console.error('Create seller profile error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Create seller profile error:', error);
    return { success: false, error: '판매자 프로필 생성 중 오류가 발생했습니다.' };
  }
}

/**
 * 판매자 프로필 조회 (user_id 기준)
 */
export async function getSellerProfile(
  userId: string
): Promise<{ success: boolean; data?: SellerProfile; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('sellers')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Get seller profile error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Get seller profile error:', error);
    return { success: false, error: '판매자 프로필을 불러오는 중 오류가 발생했습니다.' };
  }
}

/**
 * 판매자 프로필 업데이트
 */
export async function updateSellerProfile(
  userId: string,
  updates: SellerUpdate
): Promise<{ success: boolean; data?: SellerProfile; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('sellers')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Update seller profile error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Update seller profile error:', error);
    return { success: false, error: '판매자 프로필 업데이트 중 오류가 발생했습니다.' };
  }
}

/**
 * 판매자 상태 변경 (관리자용)
 */
export async function updateSellerStatus(
  userId: string,
  status: 'pending_approval' | 'approved' | 'suspended' | 'rejected'
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('sellers')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (error) {
      console.error('Update seller status error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Update seller status error:', error);
    return { success: false, error: '판매자 상태 변경 중 오류가 발생했습니다.' };
  }
}

/**
 * 모든 판매자 목록 조회 (관리자용)
 */
export async function getAllSellers(
  filters?: {
    status?: 'pending_approval' | 'approved' | 'suspended' | 'rejected';
    limit?: number;
    offset?: number;
  }
): Promise<{ success: boolean; data?: SellerProfile[]; error?: string }> {
  try {
    let query = supabase
      .from('sellers')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Get all sellers error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Get all sellers error:', error);
    return { success: false, error: '판매자 목록을 불러오는 중 오류가 발생했습니다.' };
  }
}

/**
 * 판매자 프로필 삭제
 */
export async function deleteSellerProfile(
  userId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('sellers')
      .delete()
      .eq('user_id', userId);

    if (error) {
      console.error('Delete seller profile error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete seller profile error:', error);
    return { success: false, error: '판매자 프로필 삭제 중 오류가 발생했습니다.' };
  }
}

// checkNicknameAvailability 함수는 제거됨
// (nickname 필드가 migration 20250124001에서 삭제됨)